import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Modal } from '@/components';
import { useOneSignal } from '@/common/hooks/useOneSignal';
import notificationIcon from '@/assets/notification.png';

/**
 * NotificationPermissionModal
 * Displays a friendly permission prompt and invokes enableNotifications()
 * strictly on user click (button gesture), ensuring Chrome never dismisses it.
 */
export function NotificationPermissionModal({ onClose } = {}) {
  const {
    permissionStatus,
    enableNotifications,
    bannerDismissed,
    dismissBanner,
  } = useOneSignal();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current browser permission status directly
  const currentPermission =
    typeof Notification !== 'undefined'
      ? Notification.permission
      : permissionStatus;

  // Return null only if already granted or denied, or if modal was closed
  if (
    location.pathname === '/not-found' ||
    location.pathname === '/server-error' ||
    !isOpen ||
    bannerDismissed ||
    currentPermission === 'granted' ||
    currentPermission === 'denied'
  ) {
    return null;
  }

  const handleClose = () => {
    dismissBanner();
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleEnable = async () => {
    setIsSubmitting(true);
    try {
      await enableNotifications();
    } catch (err) {
      console.warn('[OneSignal] enableNotifications failed:', err);
    } finally {
      setIsSubmitting(false);
      setIsOpen(false);
      if (onClose) onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      showClose={true}
      size="sm"
      panelClassName="p-5 sm:p-6 rounded-[24px]"
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
          <img
            src={notificationIcon}
            alt="Notification"
            className="w-7 h-7 object-contain"
          />
        </div>

        <h3 className="text-[20px] font-medium text-[#1F2241] leading-snug">
          Enable Notifications
        </h3>

        <p className="text-[14px] text-[#868686] mt-2 max-w-[280px] leading-normal font-normal">
          Get real-time push alerts on your desktop for new leads, chats, and
          assignments.
        </p>

        <div className="flex items-center gap-3 mt-6 w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 h-11 rounded-[12px] text-sm font-semibold"
          >
            Later
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleEnable}
            disabled={isSubmitting}
            className="flex-1 h-11 rounded-[12px] text-sm font-semibold"
          >
            {isSubmitting ? 'Enabling...' : 'Enable'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
